import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "wouter";
import { TherapistWithSpecialties, Availability } from "@shared/schema";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Loader2 } from "lucide-react";
import { useState } from "react";

export default function TherapistProfile() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [availableTimes, setAvailableTimes] = useState<Date[]>([]);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  
  // Fetch therapist details
  const { data: therapist, isLoading: therapistLoading } = useQuery<TherapistWithSpecialties>({
    queryKey: [`/api/therapists/${id}`],
  });
  
  // Fetch therapist availabilities
  const { data: availabilities, isLoading: availabilitiesLoading } = useQuery<Availability[]>({
    queryKey: [`/api/therapists/${id}/availabilities`],
    onSuccess: (data) => {
      // If no date is selected yet, don't show any times
      if (!selectedDate) return;
      
      // Filter availabilities for the selected date
      const selectedDateStr = selectedDate.toDateString();
      const timesForSelectedDate = data
        .filter(a => new Date(a.date).toDateString() === selectedDateStr)
        .map(a => new Date(a.date));
      
      setAvailableTimes(timesForSelectedDate);
    },
  });
  
  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(null);
    
    if (!date || !availabilities) {
      setAvailableTimes([]);
      return;
    }
    
    // Filter availabilities for the selected date
    const selectedDateStr = date.toDateString();
    const timesForSelectedDate = availabilities
      .filter(a => new Date(a.date).toDateString() === selectedDateStr)
      .map(a => new Date(a.date));
    
    setAvailableTimes(timesForSelectedDate);
  };
  
  // Handle booking
  const handleBook = () => {
    if (!selectedTime) return;
    navigate(`/booking?therapistId=${id}&date=${selectedTime.toISOString()}`);
  };
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };
  
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };
  
  // Calculate available dates for the calendar
  const availableDates = availabilities?.reduce((dates: Date[], availability) => {
    const date = new Date(availability.date);
    const dateString = date.toDateString();
    
    // Add the date if it's not already in the array
    if (!dates.some(d => d.toDateString() === dateString)) {
      dates.push(date);
    }
    
    return dates;
  }, []);
  
  const isDateAvailable = (date: Date) => {
    return availableDates?.some(d => d.toDateString() === date.toDateString());
  };
  
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating / 10);
    const hasHalfStar = rating % 10 >= 5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className="fas fa-star text-yellow-400"></i>);
    }
    
    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt text-yellow-400"></i>);
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star text-yellow-400"></i>);
    }
    
    return stars;
  };
  
  if (therapistLoading || availabilitiesLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }
  
  if (!therapist) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Terapeuta não encontrado</h2>
            <Link href="/therapists">
              <Button>Voltar para terapeutas</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow bg-neutral">
        <section className="container mx-auto px-4 py-8 md:py-12">
          <div className="mb-6">
            <Link href="/therapists">
              <a className="text-primary hover:text-secondary inline-flex items-center">
                <i className="fas fa-chevron-left text-xs mr-2"></i> Voltar para terapeutas
              </a>
            </Link>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-8">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/3">
                <img 
                  src={therapist.image_url}
                  alt={therapist.name} 
                  className="w-full h-auto object-cover rounded-lg shadow-sm"
                />
                
                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-2">{therapist.name}</h3>
                  <p className="text-gray-600 mb-2">{therapist.title}</p>
                  
                  <div className="flex items-center mb-4">
                    <div className="flex mr-2">
                      {renderStars(therapist.rating)}
                    </div>
                    <span className="text-sm text-gray-600">
                      {(therapist.rating / 10).toFixed(1)} ({therapist.review_count} avaliações)
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Especialidades</h4>
                    <div className="flex flex-wrap gap-2">
                      {therapist.specialties.map(specialty => (
                        <Badge key={specialty.id} variant="outline" className="bg-primary/10 text-primary font-normal">
                          {specialty.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Valor da sessão</h4>
                    <p className="text-lg font-semibold text-primary">
                      {formatCurrency(therapist.hourly_rate)}
                      <span className="text-sm font-normal text-gray-600 ml-1">/sessão</span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="md:w-2/3">
                <div className="mb-6">
                  <h2 className="text-2xl font-heading font-bold mb-4">Sobre</h2>
                  <p className="text-gray-700 leading-relaxed">{therapist.bio}</p>
                </div>
                
                <div>
                  <h2 className="text-2xl font-heading font-bold mb-4">Agende uma sessão</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">1. Escolha uma data</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          disabled={(date) => {
                            // Disable dates in the past
                            const yesterday = new Date();
                            yesterday.setDate(yesterday.getDate() - 1);
                            const isPast = date < yesterday;
                            
                            // Disable dates with no availability
                            const hasAvailability = isDateAvailable(date);
                            
                            return isPast || !hasAvailability;
                          }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        2. Escolha um horário
                        {selectedDate && ` para ${selectedDate.toLocaleDateString('pt-BR')}`}
                      </h3>
                      {!selectedDate ? (
                        <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                          <p className="text-gray-500">Selecione uma data primeiro</p>
                        </div>
                      ) : availableTimes.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                          <p className="text-gray-500">Não há horários disponíveis para esta data</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {availableTimes.map((time, index) => (
                            <button
                              key={index}
                              className={`border rounded-md p-2 text-center transition-colors ${
                                selectedTime && selectedTime.getTime() === time.getTime()
                                  ? "border-primary bg-primary/5 font-medium"
                                  : "border-gray-200 hover:border-primary"
                              }`}
                              onClick={() => setSelectedTime(time)}
                            >
                              {formatTime(time)}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-6">
                        <Button
                          onClick={handleBook}
                          disabled={!selectedTime}
                          className="w-full"
                        >
                          {selectedTime ? "Agendar Sessão" : "Selecione um horário"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
